import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import type { Listing, SaleType } from '@thevillages/schemas';

import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';

import type { Map2 } from './map-2';

import { ShortPrice } from './utils/short-price';

// Tracks the ObjectID assigned to each listing so subsequent updates can be applied as
// incremental adds/updates/deletes instead of clearing and rebuilding the whole layer.
export interface LayerState {
  idToObjectId: Map<string, number>;
  nextObjectId: number;
}

export function UpdateListingLayers(this: Map2): void {
  if (!this.stateService.mapViewCreated()) {
    return;
  }

  const _bySaleType = new Map<SaleType, Listing[]>();

  for (const _listing of this.listings()) {
    const _group = _bySaleType.get(_listing.saleType);

    if (_group) {
      _group.push(_listing);
    } else {
      _bySaleType.set(_listing.saleType, [_listing]);
    }
  }

  for (const [_saleType, _layer] of this.saleTypeLayers) {
    const _state = this.layerState.get(_saleType);

    if (!_state) {
      continue;
    }

    applyListingEdits(_layer, _state, _bySaleType.get(_saleType) ?? []);
  }
}

function applyListingEdits(layer: FeatureLayer, state: LayerState, listings: Listing[]): void {
  const _addFeatures: Graphic[] = [];
  const _updateFeatures: Graphic[] = [];
  const _deleteFeatures: Graphic[] = [];
  const _newIds = new Set<string>();

  for (const _listing of listings) {
    if (!_listing.id) {
      continue;
    }

    _newIds.add(_listing.id);

    const _existingObjectId = state.idToObjectId.get(_listing.id);
    const _objectId = _existingObjectId ?? state.nextObjectId++;
    const _graphic = createGraphic(_listing, _objectId);

    if (_existingObjectId === undefined) {
      state.idToObjectId.set(_listing.id, _objectId);
      _addFeatures.push(_graphic);
    } else {
      _updateFeatures.push(_graphic);
    }
  }

  for (const [_id, _objectId] of state.idToObjectId) {
    if (_newIds.has(_id)) {
      continue;
    }

    _deleteFeatures.push(new Graphic({ attributes: { ObjectID: _objectId } }));
    state.idToObjectId.delete(_id);
  }

  if (!_addFeatures.length && !_updateFeatures.length && !_deleteFeatures.length) {
    return;
  }

  void layer.applyEdits({
    addFeatures: _addFeatures,
    deleteFeatures: _deleteFeatures,
    updateFeatures: _updateFeatures,
  });
}

function createGraphic(listing: Listing, objectId: number): Graphic {
  return new Graphic({
    attributes: {
      id: listing.id,
      ObjectID: objectId,
      pending: listing.pending ? 1 : 0,
      price: ShortPrice(listing.price),
      saved: listing.savedItemId ? 1 : 0,
    },
    geometry: new Point({
      latitude: Number(listing.geo.coordinates[1]),
      longitude: Number(listing.geo.coordinates[0]),
    }),
  });
}
